import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useOrg } from "../lib/org";
import { Card, PageHeader, Spinner, ErrorState, Money } from "../components/ui";
import type { DashboardSummary } from "@shared/types";

function Kpi({
  label,
  cents,
  currency,
  accent,
}: {
  label: string;
  cents: number;
  currency: string;
  accent?: string;
}) {
  return (
    <Card className="p-5">
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className={`mt-2 text-2xl font-semibold ${accent ?? "text-gray-900"}`}>
        <Money cents={cents} currency={currency} />
      </p>
    </Card>
  );
}

export default function Dashboard() {
  const { currentOrg } = useOrg();
  const currency = currentOrg?.baseCurrency ?? "USD";
  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard", currentOrg?.id],
    queryFn: () => api.get<DashboardSummary>("/reports/dashboard"),
    enabled: !!currentOrg,
  });

  return (
    <div>
      <PageHeader
        title={`Welcome back`}
        subtitle={currentOrg ? `${currentOrg.name} · financial overview` : ""}
      />
      {isLoading && <Spinner />}
      {error && <ErrorState error={error} />}
      {data && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="Cash on hand" cents={data.cashBalance} currency={currency} />
            <Kpi label="Accounts Receivable" cents={data.arBalance} currency={currency} />
            <Kpi label="Accounts Payable" cents={data.apBalance} currency={currency} />
            <Kpi
              label="Net income (this month)"
              cents={data.netIncomeThisMonth}
              currency={currency}
              accent={data.netIncomeThisMonth >= 0 ? "text-brand-600" : "text-red-600"}
            />
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card className="p-5 lg:col-span-2">
              <h2 className="mb-4 text-sm font-semibold text-gray-700">
                Income vs. expenses (this month)
              </h2>
              <div className="space-y-3">
                <Bar
                  label="Income"
                  cents={data.incomeThisMonth}
                  max={Math.max(data.incomeThisMonth, data.expenseThisMonth, 1)}
                  color="bg-brand-500"
                  currency={currency}
                />
                <Bar
                  label="Expenses"
                  cents={data.expenseThisMonth}
                  max={Math.max(data.incomeThisMonth, data.expenseThisMonth, 1)}
                  color="bg-amber-500"
                  currency={currency}
                />
              </div>
            </Card>
            <Card className="flex flex-col justify-center gap-4 p-5">
              <div>
                <p className="text-sm text-gray-500">Open invoices</p>
                <p className="text-3xl font-semibold">{data.openInvoiceCount}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Overdue invoices</p>
                <p className="text-3xl font-semibold text-red-600">
                  {data.overdueInvoiceCount}
                </p>
              </div>
              <Link
                to="/invoices"
                className="text-sm font-medium text-brand-600 hover:underline"
              >
                View invoices →
              </Link>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function Bar({
  label,
  cents,
  max,
  color,
  currency,
}: {
  label: string;
  cents: number;
  max: number;
  color: string;
  currency: string;
}) {
  const pct = Math.min(100, (cents / max) * 100);
  return (
    <div>
      <div className="mb-1 flex justify-between text-sm">
        <span className="text-gray-600">{label}</span>
        <Money cents={cents} currency={currency} className="font-medium" />
      </div>
      <div className="h-3 w-full overflow-hidden rounded-full bg-gray-100">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
